import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, AlertCircleIcon, RefreshCw } from 'lucide-react-native';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormattedResponseDisplay } from './FormattedResponseDisplay';
import { InquirySelector } from './InquirySelector';

import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Button, ButtonText, ButtonSpinner } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FORMAT_RESPONSE } from '@/constants/formatResponse';
import { type InquiryParticipant } from '@/hooks/useChat';
import { useColors } from '@/hooks/useColors';
import { useFormatResponseTool } from '@/hooks/useFormatResponseTool';
import { segmentClient } from '@/util/analytics';
const formatResponseSchema = z.object({
  expertResponse: z
    .string()
    .min(1, 'Expert response is required')
    .max(
      FORMAT_RESPONSE.MAX_TEXT_LENGTH,
      `Response exceeds maximum length of ${FORMAT_RESPONSE.MAX_TEXT_LENGTH.toLocaleString()} characters`
    ),
  additionalInstructions: z.string(),
});

export type FormatResponseFormData = z.infer<typeof formatResponseSchema>;

type FormatResponseToolProps = {
  recipient: InquiryParticipant;
  onAppendToChat: (text: string) => void;
};

export function FormatResponseTool({ recipient, onAppendToChat }: FormatResponseToolProps) {
  const { colors } = useColors();

  const {
    selectedConversationId,
    initialExpertResponse,
    hasExpertResponse,
    formattedResponse,
    originalSubmittedResponse,
    isPending,
    isError,
    errorMessage,
    progressMessage,
    handleInquirySelect,
    handleFormat,
    handleReset,
    wrapText,
  } = useFormatResponseTool({
    recipient,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormatResponseFormData>({
    defaultValues: {
      expertResponse: '',
      additionalInstructions: '',
    },
    resolver: zodResolver(formatResponseSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (initialExpertResponse) {
      setValue('expertResponse', initialExpertResponse);
    }
  }, [initialExpertResponse, setValue]);

  const onSubmit = handleSubmit((data) => {
    handleFormat(data);
  });

  const onReset = () => {
    handleReset();

    reset({
      expertResponse: initialExpertResponse ?? '',
      additionalInstructions: '',
    });
  };

  const handleRetry = () => {
    segmentClient.track(ANALYTICS_EVENTS.FORMAT_RESPONSE_RETRY_CLICKED);

    onSubmit();
  };

  if (!selectedConversationId || !hasExpertResponse) {
    return (
      <VStack className="flex-1 p-4" space="md">
        <InquirySelector
          selectedConversationId={selectedConversationId}
          onSelect={handleInquirySelect}
        />
        <VStack className="flex-1 items-center justify-center">
          <Text size="sm" className="text-center text-typography-500">
            {!selectedConversationId
              ? 'Select an inquiry to format a response.'
              : 'No expert responses to format yet.'}
          </Text>
        </VStack>
      </VStack>
    );
  }

  if (formattedResponse) {
    return (
      <VStack className="flex-1 p-4">
        <FormattedResponseDisplay
          formattedResponse={formattedResponse}
          originalResponse={originalSubmittedResponse}
          onReset={onReset}
          onAppendToChat={onAppendToChat}
          wrapText={wrapText}
        />
      </VStack>
    );
  }

  const getSubmitButtonContent = () => {
    if (isPending) {
      return (
        <HStack space="sm" className="items-center">
          <ButtonSpinner color={colors.primary[200]} />
          <ButtonText>Formatting...</ButtonText>
        </HStack>
      );
    }

    if (isError) {
      return (
        <HStack space="sm" className="items-center">
          <Icon as={RefreshCw} size="sm" className="text-typography-0" />
          <ButtonText>Try Again</ButtonText>
        </HStack>
      );
    }

    return <ButtonText>Format Response</ButtonText>;
  };

  const submitButtonContent = getSubmitButtonContent();

  return (
    <VStack className="flex-1 p-4" space="md">
      <InquirySelector
        selectedConversationId={selectedConversationId}
        onSelect={handleInquirySelect}
        isDisabled={isPending}
      />

      <Controller
        name="expertResponse"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControl isInvalid={!!errors.expertResponse?.message} className="flex-1">
            <FormControlLabel className="mb-1">
              <FormControlLabelText size="sm">Expert Response</FormControlLabelText>
            </FormControlLabel>

            <Textarea className="flex-1" isDisabled={isPending}>
              <TextareaInput
                className="text-base text-typography-900"
                placeholder="Expert response text..."
                value={value}
                onChangeText={onChange}
                multiline
              />
            </Textarea>

            <FormControlError className="mt-1">
              <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
              <FormControlErrorText size="xs">
                {errors.expertResponse?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        )}
      />

      <Controller
        name="additionalInstructions"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControl isInvalid={!!errors.additionalInstructions?.message}>
            <FormControlLabel className="mb-1">
              <FormControlLabelText size="sm">Additional Instructions</FormControlLabelText>
            </FormControlLabel>

            <Textarea className="min-h-[100px]" isDisabled={isPending}>
              <TextareaInput
                className="text-base text-typography-900"
                placeholder="Add any specific instructions for formatting..."
                value={value}
                onChangeText={onChange}
                multiline
              />
            </Textarea>

            <FormControlError className="mt-1">
              <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
              <FormControlErrorText size="xs">
                {errors.additionalInstructions?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        )}
      />

      {isError && errorMessage && (
        <Alert action="error" variant="outline">
          <AlertIcon as={AlertCircle} />
          <AlertText size="sm">{errorMessage}</AlertText>
        </Alert>
      )}

      {isPending && progressMessage && (
        <HStack space="sm" className="items-center justify-center py-2">
          <Text size="sm" className="italic text-typography-500">
            {progressMessage}
          </Text>
        </HStack>
      )}

      <HStack space="sm">
        {isError && (
          <Button size="sm" variant="outline" onPress={onReset} className="flex-1">
            <ButtonText>Reset</ButtonText>
          </Button>
        )}

        <Button
          size="sm"
          onPress={isError ? handleRetry : onSubmit}
          disabled={isPending}
          className={isError ? 'flex-1' : 'w-full'}>
          {submitButtonContent}
        </Button>
      </HStack>
    </VStack>
  );
}
